import {Injectable} from '@angular/core';
import {BimSection, BimSectionPlan} from '../../../bp-common/models/plan';
import {ViewerReadyParams} from '@mydocma/bim-viewer';
import {LatLng, Point} from 'leaflet';
import {PlanService, TileViewerReadyEvent} from '@mydocma/tile-viewer';
import {PlanLocationService} from './plan-location.service';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Subscription} from 'rxjs';
import {
  CoordinatesTransformationService
} from '../coordinates-transformation-service/coordinates-transformation.service';
import {CameraPosition} from '@mydocma/bim-viewer';
import {CameraChangeInfo} from '@mydocma/bim-viewer';

export type planId = string;

@UntilDestroy()
@Injectable()
export class LocationService {
  public planReady: TileViewerReadyEvent;
  // Camera Functions
  public planLocationCollection = new Map<planId, CameraPosition>();
  private bimSection: BimSection;
  private bimPlan: BimSectionPlan;
  private bimViewerReadParamData: ViewerReadyParams;
  private markerLocationChangeSubscription: Subscription;
  private cameraRotationChangeSubscription: Subscription;
  private planService: PlanService;


  constructor(
    private planLocationService: PlanLocationService,
    private coordinatesTransformationService: CoordinatesTransformationService,
  ) {

  }

  attach(planReady, bimViewerReadParamData, {bimPlan, section}) {
    this.bimViewerReadParamData = bimViewerReadParamData;
    this.planReady = planReady;
    this.planService = this.planReady.mapService as PlanService;
    this.bimPlan = bimPlan;
    this.bimSection = section;
  }


  public setLocationPoint(): void {
    const initialCamera = this.getInitialCamera();
    const initialLocationPoint = this.coordinatesTransformationService.transformToPlanCoordinates(initialCamera.position);

    this.updatePlanLocationCollection(initialCamera);
    this.updatePinAndCameraLocations(initialLocationPoint, initialCamera);

    this.setupMarkerLocationChangeSubscription();
  }

  private getInitialCamera(): CameraPosition {
    const cachedCamera = this.planLocationCollection.get(this.bimPlan.planId);
    if (cachedCamera) {
      return cachedCamera;
    }

    const centerLatLng = this.planService.visiblePlanBounds.getCenter();
    const centerPoint = this.getCenterPoint(centerLatLng, this.planService.plan.maxZoom);
    const centerBimPoint = this.coordinatesTransformationService.transformToModelCoordinates(centerPoint);
    return {
      target: {
        x: centerBimPoint.x,
        y: centerBimPoint.y + 1,
        z: this.bimSection.z
      },
      position: {
        ...centerBimPoint,
        z: this.bimSection.z
      }
    };
  }


  private updatePlanLocationCollection(camera: CameraPosition): void {
    this.planLocationCollection.set(this.bimPlan.planId, camera);
  }

  private async updatePinAndCameraLocations(planPoint, camera: CameraPosition): Promise<void> {
    const cameraService = this.bimViewerReadParamData.cameraService;
    const viewerService = this.bimViewerReadParamData.viewerService;
    this.planLocationService.setLocationMarker(planPoint);

    const viewer = viewerService.forgeViewer;
    if (!this.isModelFullyLoaded(viewer)) {
      viewerService.loaderService.showLoader();
      await this.waitForModelFullyLoaded(viewer);
      viewerService.loaderService.hideLoader();
    }

    cameraService.setCameraPosition(camera);
    cameraService.enableViewerBehaveLikePanorama();
  }

  private isModelFullyLoaded(viewer: any): boolean {
    const model = viewer?.model;
    return !!model && !!model.isLoadDone?.() && (!!model.is2d?.() || !!model.getInstanceTree?.());
  }

  private waitForModelFullyLoaded(viewer: any): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.isModelFullyLoaded(viewer)) {
        resolve();
        return;
      }
      const viewing = (window as any).Autodesk?.Viewing;
      const GEOMETRY_LOADED = viewing?.GEOMETRY_LOADED_EVENT ?? 'geometryLoaded';
      const OBJECT_TREE_CREATED = viewing?.OBJECT_TREE_CREATED_EVENT ?? 'objectTreeCreated';

      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timeoutId);
        viewer?.removeEventListener(GEOMETRY_LOADED, check);
        viewer?.removeEventListener(OBJECT_TREE_CREATED, check);
        resolve();
      };
      const check = () => {
        if (this.isModelFullyLoaded(viewer)) {
          finish();
        }
      };
      const timeoutId = setTimeout(finish, 60000);

      viewer?.addEventListener(GEOMETRY_LOADED, check);
      viewer?.addEventListener(OBJECT_TREE_CREATED, check);
      check();
    });
  }


  private getCenterPoint(centerLatLng: LatLng, maxZoom: number): Point {
    return this.planService.map.project(centerLatLng, maxZoom);
  }

  private setupMarkerLocationChangeSubscription(): void {
    this.markerLocationChangeSubscription?.unsubscribe();
    this.cameraRotationChangeSubscription?.unsubscribe();
    this.markerLocationChangeSubscription = this.planLocationService.onMarkerLocationChange
      .subscribe((point: Point) => {
        this.setNewCameraPosition(point);
      });

    this.cameraRotationChangeSubscription = this.bimViewerReadParamData.cameraService.onCameraRotateChange
      .subscribe((value: CameraChangeInfo) => {
        this.handleCameraRotationChange(value);
      });
  }

  private normalizeAngleDegrees(angle: number): number {
    return ((angle + 180) % 360 + 360) % 360 - 180;
  }

// how much plan rotated relative to bim
  private getPlanCalibrationRotationDegrees(): number {
    const transform = this.bimPlan?.transform;

    if (
      !transform?.plan1Point1 ||
      !transform?.plan1Point2 ||
      !transform?.plan2Point1 ||
      !transform?.plan2Point2
    ) {
      return 0;
    }

    const bimAngle = Math.atan2(
      transform.plan1Point2.y - transform.plan1Point1.y,
      transform.plan1Point2.x - transform.plan1Point1.x
    );

    const planAngle = Math.atan2(
      -(transform.plan2Point2.y - transform.plan2Point1.y),
      transform.plan2Point2.x - transform.plan2Point1.x
    );

    return this.normalizeAngleDegrees((planAngle - bimAngle) * 180 / Math.PI);
  }

  private handleCameraRotationChange(value: CameraChangeInfo): void {
    this.planLocationCollection.set(this.bimPlan.planId, value.camera);

    const cameraHeading = this.getCameraAngleDegreesFromPositionAndTarget(value.camera);
    const calibrationRotation = this.getPlanCalibrationRotationDegrees();


    const totalAngle = this.normalizeAngleDegrees(
      cameraHeading + calibrationRotation
    );
    this.planLocationService.setLocationMarkerAngle(totalAngle);
  }

  private getCameraAngleDegreesFromPositionAndTarget(camera: {
    position: { x: number; y: number; z: number };
    target: { x: number; y: number; z: number };
  }): number {
    // how much left
    const dx = camera.target.x - camera.position.x;
    // how much up down
    const dy = camera.target.y - camera.position.y;
    // angle in radians
    const angleRadians = Math.atan2(dy, dx);
    // radians to 360
    return angleRadians * 180 / Math.PI;
  }

  private setNewCameraPosition(point: Point) {
    const position = this.coordinatesTransformationService.transformToModelCoordinates(point);
    const cameraPosition = this.planLocationCollection.get(this.bimPlan.planId);
    if (cameraPosition) {
      const oldPosition = new THREE.Vector3(cameraPosition.position.x, cameraPosition.position.y, cameraPosition.position.z);
      cameraPosition.position = {...position, z: this.bimSection.z};
      const oldTarget = new THREE.Vector3(cameraPosition.target.x, cameraPosition.target.y, cameraPosition.target.z);
      const newPosition = new THREE.Vector3(cameraPosition.position.x, cameraPosition.position.y, cameraPosition.position.z);
      const translationVector = newPosition.sub(oldPosition); // {x: 1, y: 1, z: 1}
      const newTarget = oldTarget.add(translationVector); //
      cameraPosition.target = newTarget;
    } else {
      // cameraPosition = {...position};
    }
    this.bimViewerReadParamData.cameraService.setCameraPosition(cameraPosition);
    this.planLocationCollection.set(this.bimPlan.planId, cameraPosition);
  }
}
